import { useGTM } from '@/hooks/useGTM';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';

// Map custom events to Facebook standard events
const getFacebookEventName = (eventName: string): string => {
  const eventMap: Record<string, string> = {
    'sign_up_start': 'Lead',
    'sign_up_complete': 'CompleteRegistration',
    'add_to_cart': 'AddToCart',
    'begin_checkout': 'InitiateCheckout',
    'purchase': 'Purchase',
    'free_trial_start': 'StartTrial',
    'demo_view': 'ViewContent',
  };
  return eventMap[eventName] || eventName;
};

export const useConversionTracking = () => {
  const { pushEvent: gtmPush } = useGTM();
  const { trackEvent: gaTrack } = useAnalytics();
  const { trackEvent: fbTrack } = useFacebookPixel();

  // Track all platforms simultaneously
  const trackConversion = (eventName: string, data?: Record<string, any>) => {
    gtmPush(eventName, data);
    gaTrack(eventName, data);
    
    // Map to Facebook standard event name
    const fbEventName = getFacebookEventName(eventName);
    fbTrack(fbEventName, data);
  };

  return {
    // Sign up events
    trackSignUpStart: () => trackConversion('sign_up_start'),
    trackSignUpComplete: (userId: string) => 
      trackConversion('sign_up_complete', { user_id: userId }),
    
    // Purchase events
    trackPurchaseIntent: (contentName?: string, contentIds?: string[]) => 
      trackConversion('add_to_cart', { 
        content_name: contentName || 'Subscription',
        content_ids: contentIds || ['subscription'],
        content_type: 'product'
      }),
    trackCheckoutStart: (value?: number, currency: string = 'USD') => 
      trackConversion('begin_checkout', { 
        value, 
        currency,
        content_type: 'product'
      }),
    trackPurchaseComplete: (
      value: number, 
      currency: string = 'USD',
      contentName?: string,
      contentIds?: string[],
      numItems: number = 1
    ) =>
      trackConversion('purchase', { 
        value, 
        currency,
        content_name: contentName || 'Subscription Purchase',
        content_category: 'subscription',
        content_ids: contentIds || ['subscription'],
        content_type: 'product',
        num_items: numItems
      }),
    
    // Engagement events
    trackLessonStart: (lessonId: string, moduleName: string) =>
      trackConversion('lesson_start', { lesson_id: lessonId, module_name: moduleName }),
    trackLessonComplete: (lessonId: string, moduleName: string) =>
      trackConversion('lesson_complete', { lesson_id: lessonId, module_name: moduleName }),
    trackQuizComplete: (lessonId: string, score: number) =>
      trackConversion('quiz_complete', { lesson_id: lessonId, score }),
    
    // CTA clicks
    trackCTAClick: (ctaLocation: string, ctaText: string) =>
      trackConversion('cta_click', { location: ctaLocation, text: ctaText }),
    
    // Free trial
    trackFreeTrial: () => trackConversion('free_trial_start'),
    
    // Video/Demo views
    trackDemoView: (contentName?: string) => 
      trackConversion('demo_view', {
        content_name: contentName || 'Quest Demo',
        content_category: 'demo',
        content_type: 'video'
      }),
  };
};
